import { defineStore } from 'pinia'

import { describeColumns, mergePreferenceGroup, type ColumnDescriptor } from '@/utils/columnLayout'
import {
  createColumnPreferenceStorage,
  parseColumnWidths,
  uniqueStringArray,
  type ColumnPreferenceScope as SharedColumnPreferenceScope,
  type ColumnPreferenceWriteResult as SharedColumnPreferenceWriteResult,
} from '@/utils/columnPreferenceStorage'

const STORAGE_PREFIX = 'moria:review-workbook-columns:v1'

export interface WorkbookColumnPreference {
  order: string[]
  fixed: string[]
  widths?: Record<string, number>
}

export type WorkbookColumnDescriptor = ColumnDescriptor

export type WorkbookColumnPreferenceScope = SharedColumnPreferenceScope<WorkbookColumnPreference>
export type WorkbookColumnPreferenceWriteResult =
  SharedColumnPreferenceWriteResult<WorkbookColumnPreference>

export type WorkbookColumnPreferenceCommand =
  | { type: 'save'; order: string[]; fixed: string[] }
  | { type: 'reset' }

export interface ReconciledColumnLayout {
  columns: WorkbookColumnDescriptor[]
  fixedKeys: string[]
  fixedCount: number
}

function parsePreference(raw: string): WorkbookColumnPreference | null {
  try {
    const candidate = JSON.parse(raw) as Partial<WorkbookColumnPreference>
    const order = uniqueStringArray(candidate.order)
    const fixed = uniqueStringArray(candidate.fixed)
    const widths = parseColumnWidths(candidate.widths)
    if (!order || !fixed || !widths) return null
    const orderKeys = new Set(order)
    if (fixed.some((key) => !orderKeys.has(key))) return null
    return { order, fixed, widths }
  } catch {
    return null
  }
}

function clonePreference(
  preference: WorkbookColumnPreference | null,
): WorkbookColumnPreference | null {
  return preference
    ? {
        order: [...preference.order],
        fixed: [...preference.fixed],
        widths: { ...preference.widths },
      }
    : null
}

export function describeWorkbookColumns(headers: string[]): WorkbookColumnDescriptor[] {
  return describeColumns(headers)
}

export function reconcileColumnPreference(
  preference: WorkbookColumnPreference | null,
  descriptors: WorkbookColumnDescriptor[],
): ReconciledColumnLayout {
  const byKey = new Map(descriptors.map((descriptor) => [descriptor.key, descriptor]))
  const fixedSet = new Set(preference?.fixed ?? [])
  const savedOrder = preference?.order ?? []
  const matchedKeys = savedOrder.filter((key) => byKey.has(key))
  const matchedSet = new Set(matchedKeys)
  const fixedKeys = matchedKeys.filter((key) => fixedSet.has(key))
  const scrollableKeys = matchedKeys.filter((key) => !fixedSet.has(key))
  for (const descriptor of descriptors) {
    if (!matchedSet.has(descriptor.key)) scrollableKeys.push(descriptor.key)
  }

  if (fixedKeys.length === descriptors.length && fixedKeys.length > 0) {
    scrollableKeys.unshift(fixedKeys.pop()!)
  }

  const orderedKeys = [...fixedKeys, ...scrollableKeys]
  return {
    columns: orderedKeys.map((key) => byKey.get(key)!).filter(Boolean),
    fixedKeys,
    fixedCount: fixedKeys.length,
  }
}

export function mergeVisibleColumnPreference(
  current: WorkbookColumnPreference | null,
  visibleOrder: string[],
  visibleFixed: string[],
): WorkbookColumnPreference {
  const desiredOrder = [...new Set(visibleOrder)]
  const visibleKeys = new Set(desiredOrder)
  const fixedKeys = new Set(visibleFixed.filter((key) => visibleKeys.has(key)))
  const currentFixed = new Set(current?.fixed ?? [])
  const currentFixedGroup = (current?.order ?? []).filter((key) => currentFixed.has(key))
  const currentScrollableGroup = (current?.order ?? []).filter((key) => !currentFixed.has(key))
  const desiredFixedGroup = desiredOrder.filter((key) => fixedKeys.has(key))
  const desiredScrollableGroup = desiredOrder.filter((key) => !fixedKeys.has(key))

  const fixedGroup = mergePreferenceGroup(currentFixedGroup, visibleKeys, desiredFixedGroup)
  const scrollableGroup = mergePreferenceGroup(
    currentScrollableGroup,
    visibleKeys,
    desiredScrollableGroup,
  )
  return {
    order: [...fixedGroup, ...scrollableGroup],
    fixed: fixedGroup,
  }
}

export const useWorkbookColumnPreferencesStore = defineStore('workbookColumnPreferences', () => {
  const persistence = createColumnPreferenceStorage(
    STORAGE_PREFIX,
    parsePreference,
    clonePreference,
    'workbook',
  )

  async function saveVisibleLayout(
    userId: string,
    projectId: number,
    visibleOrder: string[],
    visibleFixed: string[],
  ): Promise<WorkbookColumnPreferenceWriteResult> {
    return persistence.update(userId, projectId, (current) => ({
      ...mergeVisibleColumnPreference(current, visibleOrder, visibleFixed),
      widths: { ...current?.widths },
    }))
  }

  async function saveWidth(
    userId: string,
    projectId: number,
    key: string,
    width: number,
  ): Promise<WorkbookColumnPreferenceWriteResult> {
    return persistence.update(userId, projectId, (current) => ({
      order: current?.order ?? [],
      fixed: current?.fixed ?? [],
      widths: { ...current?.widths, [key]: Math.max(24, Math.min(5000, Math.round(width))) },
    }))
  }

  return {
    load: persistence.load,
    saveVisibleLayout,
    reset: persistence.reset,
    hydrate: persistence.hydrate,
    retry: persistence.retry,
    clear: persistence.clear,
    saveWidth,
  }
})
